import React, { useEffect, useId, useMemo, useState } from 'react';
import { Database, Globe, HardDrive, Lock } from 'lucide-react';
import { Dialog } from '../ui/Dialog';
import { Segmented } from '../ui/Segmented';
import { Checkbox } from '../ui/Checkbox';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { groupLabsByRegion, useSources } from '../../context/SourcesContext';
import { formatDate } from '../../lib/format';
import { cx } from '../../lib/cx';

const REGION_NAMES = {
  VIC: 'Victoria', NSW: 'New South Wales', QLD: 'Queensland', SA: 'South Australia', WA: 'Western Australia',
  TAS: 'Tasmania', ACT: 'ACT', NT: 'Northern Territory', National: 'National & multi-state',
};

const LabOption = ({ lab, checked, disabled, onToggle }) => {
  const id = useId();
  return (
    <li className={cx('flex items-start gap-3 rounded-lg px-2 py-2', !disabled && 'hover:bg-raised/60')}>
      <Checkbox id={id} checked={checked && !disabled} disabled={disabled} onChange={onToggle} className="mt-0.5" />
      <label htmlFor={id} className={cx('min-w-0 flex-1', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}>
        <span className={cx('block text-sm', disabled ? 'text-faint' : 'text-fg')}>{lab.name}</span>
        <span className="block text-xs text-muted">
          {lab.liveOnly
            ? (disabled ? 'Live website only · not available for your role' : 'Live website only (slower)')
            : `${lab.recordCount.toLocaleString()} tests · updated ${formatDate(lab.dataTimestamp)}`}
        </span>
      </label>
      {lab.liveOnly && <Badge tone={disabled ? 'neutral' : 'info'} icon={disabled ? Lock : Globe}>Live</Badge>}
    </li>
  );
};

/**
 * Where and how to search: cached vs live, titles vs full text, and which
 * labs. Changes apply on "Apply" (and re-run the current search).
 */
export const ScopeDialog = ({ open, onClose, mode, match, onApply }) => {
  const { labs, customFolders, scope, canSearchLive, isAvailable } = useSources();
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (open) {
      setDraft({
        mode: canSearchLive ? mode : 'cached',
        match,
        excludedLabs: new Set(scope.excludedLabs),
        excludedFolders: new Set(scope.excludedFolders),
      });
    }
  }, [open, mode, match, scope, canSearchLive]);

  const regions = useMemo(() => groupLabsByRegion(labs), [labs]);

  if (!draft) return null;

  const available = labs.filter((lab) => isAvailable(lab, draft.mode));
  const selectedCount = available.filter((lab) => !draft.excludedLabs.has(lab.id)).length;
  const folderCount = draft.mode === 'cached'
    ? customFolders.filter((f) => !draft.excludedFolders.has(f)).length
    : 0;
  const nothingSelected = selectedCount + folderCount === 0;

  const update = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const toggleIn = (setKey, value, include) => {
    const next = new Set(draft[setKey]);
    if (include) next.delete(value);
    else next.add(value);
    update({ [setKey]: next });
  };
  const setLabs = (ids, include) => {
    const next = new Set(draft.excludedLabs);
    ids.forEach((id) => (include ? next.delete(id) : next.add(id)));
    update({ excludedLabs: next });
  };

  const apply = () => {
    onApply({
      scope: {
        ...scope,
        mode: draft.mode,
        match: draft.match,
        excludedLabs: Array.from(draft.excludedLabs),
        excludedFolders: Array.from(draft.excludedFolders),
      },
      mode: draft.mode,
      match: draft.match,
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Search scope"
      description="Choose where to search. Saved for your next searches."
      size="lg"
      className="max-sm:!rounded-b-none"
      footer={(
        <>
          {nothingSelected && <p className="mr-auto text-sm text-danger" role="alert">Select at least one lab.</p>}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={apply} disabled={nothingSelected}>Apply</Button>
        </>
      )}
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <fieldset>
          <legend className="mb-2 text-sm font-medium text-fg">Search in</legend>
          <Segmented
            label="Search in"
            value={draft.mode}
            onChange={(v) => update({ mode: v })}
            options={[
              { value: 'cached', label: 'Cached databases', icon: Database },
              {
                value: 'live', label: 'Live websites', icon: Globe, disabled: !canSearchLive,
                title: canSearchLive ? undefined : 'Your role can search cached databases only',
              },
            ]}
            stretch
          />
          <p className="mt-1.5 text-xs text-muted">
            {draft.mode === 'live'
              ? 'Queries every lab website directly. Slower, but always current.'
              : 'Fast. Labs without a cached database are checked live.'}
            {!canSearchLive && ' Live search needs the "Cached + live" role.'}
          </p>
        </fieldset>
        <fieldset>
          <legend className="mb-2 text-sm font-medium text-fg">Match</legend>
          <Segmented
            label="Match"
            value={draft.mode === 'live' ? 'titles' : draft.match}
            onChange={(v) => update({ match: v })}
            options={[
              { value: 'titles', label: 'Titles & synonyms' },
              { value: 'full', label: 'Full text', disabled: draft.mode === 'live' },
            ]}
            stretch
          />
          <p className="mt-1.5 text-xs text-muted">
            {draft.mode === 'live'
              ? "Live search uses each lab's own search."
              : 'Full text also searches instructions, notes and other fields.'}
          </p>
        </fieldset>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-fg">
          Labs <span className="font-normal text-muted">
            · {selectedCount} of {available.length} selected
            {customFolders.length > 0 && draft.mode === 'cached' && ` · plus ${folderCount} custom database${folderCount === 1 ? '' : 's'}`}
          </span>
        </h3>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => setLabs(available.map((l) => l.id), true)}>Select all</Button>
          <Button size="sm" variant="ghost" onClick={() => setLabs(labs.map((l) => l.id), false)}>Clear</Button>
        </div>
      </div>

      <div className="mt-2 grid gap-x-6 gap-y-3 sm:grid-cols-2">
        {regions.map(([region, regionLabs]) => {
          const selectable = regionLabs.filter((l) => isAvailable(l, draft.mode));
          const on = selectable.filter((l) => !draft.excludedLabs.has(l.id)).length;
          return (
            <fieldset key={region} className="min-w-0">
              <legend className="flex w-full items-center gap-2 px-2 pb-1">
                <Checkbox
                  aria-label={`All labs in ${REGION_NAMES[region] || region}`}
                  checked={on > 0 && on === selectable.length}
                  indeterminate={on > 0 && on < selectable.length}
                  disabled={!selectable.length}
                  onChange={(checked) => setLabs(selectable.map((l) => l.id), checked)}
                />
                <span className="text-xs font-semibold uppercase tracking-wider text-faint">
                  {REGION_NAMES[region] || region}
                </span>
              </legend>
              <ul>
                {regionLabs.map((lab) => (
                  <LabOption
                    key={lab.id}
                    lab={lab}
                    checked={!draft.excludedLabs.has(lab.id)}
                    disabled={!isAvailable(lab, draft.mode)}
                    onToggle={(checked) => toggleIn('excludedLabs', lab.id, checked)}
                  />
                ))}
              </ul>
            </fieldset>
          );
        })}

        {customFolders.length > 0 && (
          <fieldset className="min-w-0">
            <legend className="flex items-center gap-2 px-2 pb-1 text-xs font-semibold uppercase tracking-wider text-faint">
              <HardDrive size={13} aria-hidden="true" /> Custom databases
            </legend>
            {draft.mode === 'live' && (
              <p className="px-2 pb-1 text-xs text-muted">Not searched in live mode.</p>
            )}
            <ul>
              {customFolders.map((folder) => (
                <CustomFolderOption
                  key={folder}
                  folder={folder}
                  checked={!draft.excludedFolders.has(folder)}
                  disabled={draft.mode === 'live'}
                  onToggle={(checked) => toggleIn('excludedFolders', folder, checked)}
                />
              ))}
            </ul>
          </fieldset>
        )}
      </div>
    </Dialog>
  );
};

const CustomFolderOption = ({ folder, checked, disabled, onToggle }) => {
  const id = useId();
  return (
    <li className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-raised/60">
      <Checkbox id={id} checked={checked && !disabled} disabled={disabled} onChange={onToggle} />
      <label htmlFor={id} className={cx('text-sm', disabled ? 'text-faint' : 'cursor-pointer text-fg')}>{folder}</label>
    </li>
  );
};
